import React, { useState, useEffect } from 'react';
import { BarChart2, TrendingUp, ShoppingBag, IndianRupee } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_BADGE = {
  'Picked Up': 'badge-delivered',
  Processing: 'badge-processing',
  'Ready for Pickup': 'badge-shipped',
  Cancelled: 'badge-cancelled',
};
const getStatusBadge = (status) => STATUS_BADGE[status] || 'badge-processing';

const Reports = () => {
  const { authHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/dashboard/stats`, { headers: authHeader })
      .then(r => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-[#7A1125] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="admin-reports">
      <div>
        <h1 className="font-playfair text-2xl font-bold text-[#1F1410]">Reports</h1>
        <p className="text-[#7A6A63] text-sm">Sales performance overview</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Revenue', value: `₹${(stats?.totalRevenue || 0).toLocaleString('en-IN')}`, icon: IndianRupee },
          { label: 'Total Orders', value: stats?.totalOrders || 0, icon: ShoppingBag },
          { label: 'This Week Orders', value: stats?.thisWeekOrders || 0, icon: TrendingUp },
          { label: 'This Week Revenue', value: `₹${(stats?.thisWeekRevenue || 0).toLocaleString('en-IN')}`, icon: BarChart2 },
        ].map(card => (
          <div key={card.label} className="bg-white rounded-2xl border border-[#ECE0DA] p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-[#7A6A63] uppercase tracking-wide">{card.label}</p>
              <card.icon size={16} className="text-[#7A1125]" />
            </div>
            <p className="font-playfair text-xl font-bold text-[#1F1410]">{card.value}</p>
          </div>
        ))}
      </div>

      {/* Sales chart */}
      <div className="bg-white rounded-2xl border border-[#ECE0DA] p-5 shadow-sm" data-testid="weekly-revenue-chart">
        <h3 className="font-playfair font-semibold text-[#1F1410] mb-4">Weekly Revenue</h3>
        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={stats?.salesChart || []} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#ECE0DA" />
            <XAxis dataKey="day" tick={{ fontSize: 11, fill: '#7A6A63' }} />
            <YAxis tick={{ fontSize: 11, fill: '#7A6A63' }} tickFormatter={v => `₹${v >= 1000 ? (v/1000).toFixed(0)+'k' : v}`} />
            <Tooltip formatter={v => [`₹${v.toLocaleString('en-IN')}`, 'Revenue']} />
            <Bar dataKey="revenue" fill="#7A1125" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Top selling products */}
      <div className="bg-white rounded-2xl border border-[#ECE0DA] shadow-sm overflow-hidden" data-testid="top-products-table">
        <div className="px-5 py-4 border-b border-[#ECE0DA]">
          <h3 className="font-playfair font-semibold text-[#1F1410]">Top Selling Products</h3>
        </div>
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[400px]">
          <thead>
            <tr className="bg-[#FDF8F3]">
              {['#', 'Product', 'Orders', 'Revenue'].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-[#7A6A63] uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#ECE0DA]">
            {(stats?.topProducts || []).map((p, i) => (
              <tr key={i} className="hover:bg-[#FDF8F3] transition-colors">
                <td className="px-4 py-3 text-[#7A6A63] font-mono text-xs">{i + 1}</td>
                <td className="px-4 py-3 font-medium text-[#1F1410]">{p.name}</td>
                <td className="px-4 py-3 text-[#7A6A63]">{p.orders} units</td>
                <td className="px-4 py-3 font-semibold text-[#7A1125]">₹{p.revenue?.toLocaleString('en-IN')}</td>
              </tr>
            ))}
            {(!stats?.topProducts || stats.topProducts.length === 0) && (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-[#7A6A63]">No data yet — place some orders first</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>

      {/* Status breakdown */}
      <div className="bg-white rounded-2xl border border-[#ECE0DA] p-5 shadow-sm">
        <h3 className="font-playfair font-semibold text-[#1F1410] mb-4">Order Status Breakdown</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Object.entries(stats?.statusBreakdown || {}).map(([status, count]) => (
            <div key={status} className="text-center p-4 rounded-xl bg-[#FDF8F3]">
              <p className={`text-2xl font-bold font-playfair mb-1 ${getStatusBadge(status)}`}>{count}</p>
              <p className="text-xs text-[#7A6A63] font-medium">{status}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Reports;
